import type { BuilderPricingVisibilitySettings } from "api-types/builders/builderTypes";
import type { BuilderSalesConfig } from "api-types/builders/salesConfig";
import type { ClientAddress } from "api-types/common/commonResponses";
import type { HomePlanStatus } from "api-types/homePlans/homePlanTypes";
import type { LocationLevelType } from "api-types/locations/locationLevelTypes";
import type { LotStatus } from "api-types/lots/lotTypes";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import type { Modify } from "common/utils/types";
import type { ClientBuilder } from "../builders/clientBuilderTypes";
import type { ClientLocationPlan } from "../locationPlans/locationPlansResponses";
import type { ClientLotHydrated } from "../lots/lotsResponses";
import type { ClientUserHydrated } from "../users/usersResponses";
import type { ClientLocation, ClientLocationLevel, ClientLocationWithLevel } from "./locationBaseTypes";

export type { ClientLocation, ClientLocationLevel, ClientLocationWithLevel } from "./locationBaseTypes";

interface EncodedLocationRelations {
  lotsCount: number;
  locationPlanCount: number;
  homePlanCount: number;
  salesStaff?: ClientUserHydrated;
  locationLevel?: ClientLocationLevel;
  childrenCount: number;
}

export type ClientLocationHydrated = Modify<ClientLocation, EncodedLocationRelations>;

export interface ClientLocationsPortalData {
  locations: ClientLocationHydrated[];
  builder: ClientBuilder;
  masterPlans: ClientMasterPlan[];
}

export interface ClientLocationTypesInUse {
  typesInUse: LocationLevelType[];
}

export interface ClientLocationCreationResponse {
  location: ClientLocationHydrated;
  locationPlans: ClientLocationPlan[];
}

export interface ClientLocationCreationWithMigratedChildrenResponse {
  location: ClientLocationWithLevel;
  createdLocationPlans: ClientLocationPlan[];
  migratedChildLocations: ClientLocationWithLevel[];
}

export interface ClientMigratedChildrenPreviewResponse {
  createdLocationPlans: {
    name: string;
    basePrice: number;
  }[];
  migratedChildLocations: ClientLocationWithLevel[];
}

export interface ClientLocationBaseData {
  location: ClientLocationHydrated;
  builder: ClientBuilder;
}

export interface ClientLocationPublicMapData {
  builder: {
    salesConfig: BuilderSalesConfig | null;
    pricingVisibilitySettings: BuilderPricingVisibilitySettings | null;
  };
  location: {
    name: string;
    parentId: string | null;
    parentLocationSalesMapUrl: string | null;
    mapSourceLocationId: string | null;
    locationLevelType: LocationLevelType | null;
    disableLots: boolean;
    allowHomePlans: boolean;
    showMapTab: boolean;
    logoUrl?: string | null;
    enableLogoOnMaps?: boolean | null;
    brochureSlipSheetUrl?: string | null;
    salesMapUrl?: string | null;
    salesMapUseShowroomIntro?: boolean | null;
    pricingVisibilitySettings: BuilderPricingVisibilitySettings | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  };
  descendantLocationIds: string[];
  descendantLocations: {
    id: string;
    name: string;
    locationLevelType: LocationLevelType | null;
    parentId: string | null;
  }[];
  locationPlans: {
    id: string;
    name: string;
  }[];
  homePlans: {
    id: string;
    locationPlanId: string;
    lotId?: string | null;
    lotNumber?: string | null;
  }[];
  lotsByLocation: {
    [locationId: string]: ClientLotHydrated[];
  };
  locationPricingSettings: {
    [locationId: string]: BuilderPricingVisibilitySettings | null;
  };
  /**
   * Effective display address per location, with ancestor addresses already
   * inherited. `null` means no location in that hierarchy has a complete address.
   */
  locationAddresses: {
    [locationId: string]: ClientAddress | null;
  };
}

export const locationSummaryCategoryOrder = ["testing", "contract", "prospect", "closed"] as const;
export type ClientLocationSummaryCategory = (typeof locationSummaryCategoryOrder)[number];

export const clientLocationSummaryPlanBreakdownDateRangeOrder = ["ALL", "6M", "3M", "1M"] as const;
export type ClientLocationSummaryPlanBreakdownDateRange =
  (typeof clientLocationSummaryPlanBreakdownDateRangeOrder)[number];

export const lotStatusBreakdownTypeOrder = ["none", "model", "spec"] as const;
export type ClientLotStatusBreakdownType = (typeof lotStatusBreakdownTypeOrder)[number];

export interface ClientLocationSummaryPhaseRow {
  key: HomePlanStatus | "TOTAL";
  counts: Record<ClientLocationSummaryCategory, number>;
  total: number;
}

export interface ClientLocationSummaryPlanBreakdownRow {
  planName: string;
  count: number;
  percentage: number;
}

/**
 * The phase matrix section. Always covers every home plan in scope — the date-range filter applies
 * only to `ClientLocationPlanBreakdown`, so the matrix is deliberately not part of a range entry.
 */
export interface ClientLocationHomePlansSummary {
  totalHomes: number;
  totalPlanTypes: number;
  phaseRows: ClientLocationSummaryPhaseRow[];
}

/** Plan-name breakdown for one date range. */
export interface ClientLocationPlanBreakdown {
  totalHomes: number;
  totalPlanTypes: number;
  planBreakdownRows: ClientLocationSummaryPlanBreakdownRow[];
  unusedPlanNames: string[];
}

export interface ClientLotStatusBreakdownRow {
  key: LotStatus | "TOTAL";
  counts: Record<ClientLotStatusBreakdownType, number>;
  total: number;
}

export interface ClientLotStatusBreakdownSummary {
  totalLots: number;
  rows: ClientLotStatusBreakdownRow[];
}

export interface ClientLocationSummaryHighlights {
  medianHomePrice: number | null;
  uniqueCustomers: number;
  newLeads: number;
  soldLots: number;
  totalLots: number;
}

export interface ClientLocationSummaryData {
  aggregatedAt: string;
  summary: ClientLocationHomePlansSummary;
  lotSummary: ClientLotStatusBreakdownSummary;
  highlights: ClientLocationSummaryHighlights;
  planBreakdownByRange: Record<ClientLocationSummaryPlanBreakdownDateRange, ClientLocationPlanBreakdown>;
  totalCommunityPlans: number;
  homesAssignedToLots: number;
}
